import { useState, useCallback, useRef } from 'react';
import paymentsService from '../services/paymentsService';
import { useMessageAlert } from '../components/common/MessageAlert';
import i18n from '../i18n';

/**
 * Data hook for the Payments Tracking page — list of sales challans with
 * receivables state, plus record/reverse/reminder/due-date actions.
 */
export default function usePayments() {
  const [challans, setChallans] = useState([]);
  const [total, setTotal] = useState(0);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { showError } = useMessageAlert();

  // A debounced search and a tab click can be in flight together; without a
  // sequence check the slower one can land last and paint rows that do not match
  // the filters on screen.
  const requestSeq = useRef(0);

  const fetchChallans = useCallback(async (params = {}) => {
    const seq = ++requestSeq.current;
    setLoading(true);
    setError(null);
    try {
      const result = await paymentsService.listChallans(params);
      if (seq !== requestSeq.current) return { success: true, stale: true };
      if (result.success) {
        setChallans(result.data.items || []);
        setTotal(result.data.total || 0);
        return { success: true };
      }
      // Keep the rows (and count) already on screen and say what happened
      // (policy C19): a transient failure used to blank the list.
      setError(result.error);
      showError(result.error || i18n.t('hookErrors.common.unexpected'));
      return { success: false, error: result.error };
    } catch (e) {
      if (seq !== requestSeq.current) return { success: false, stale: true };
      setError(e.message);
      showError(e.message || i18n.t('hookErrors.common.unexpected'));
      return { success: false, error: e.message };
    } finally {
      if (seq === requestSeq.current) setLoading(false);
    }
  }, []);

  const fetchSummary = useCallback(async (params = {}) => {
    const result = await paymentsService.getSummary(params);
    if (result.success) setSummary(result.data);
    return result;
  }, []);

  const recordPayment = useCallback((subOrderId, payload) =>
    paymentsService.recordPayment(subOrderId, payload), []);
  const reversePayment = useCallback((paymentId) =>
    paymentsService.reversePayment(paymentId), []);
  const getHistory = useCallback((subOrderId) =>
    paymentsService.getHistory(subOrderId), []);
  const updateDueDate = useCallback((subOrderId, payload) =>
    paymentsService.updateDueDate(subOrderId, payload), []);
  const sendReminder = useCallback((subOrderId, channels) =>
    paymentsService.sendReminder(subOrderId, channels), []);

  const bulkMarkPaid = useCallback((payload) =>
    paymentsService.bulkMarkPaid(payload), []);
  const bulkUpdateDueDate = useCallback((payload) =>
    paymentsService.bulkUpdateDueDate(payload), []);
  const bulkSendReminders = useCallback((payload) =>
    paymentsService.bulkSendReminders(payload), []);

  return {
    challans, total, summary, loading, error,
    fetchChallans, fetchSummary,
    recordPayment, reversePayment, getHistory, updateDueDate, sendReminder,
    bulkMarkPaid, bulkUpdateDueDate, bulkSendReminders,
  };
}
